import { AuthProvider, useAuth } from "@/lib/auth"
import { BackgroundWorkProvider } from "@/lib/backgroundWork"
import { LoginPage } from "@/pages/Login"
import { DashboardPage } from "@/pages/Dashboard"
import { ProfilePage } from "@/pages/Profile"
import { TodayPage } from "@/pages/Today"
import { PerformancePage } from "@/pages/Performance"
import { IngredientsPage } from "@/pages/Ingredients"
import { WeightPage } from "@/pages/Weight"
import { RecipesNewPage } from "@/pages/RecipesNew"
import { RecipesPage } from "@/pages/Recipes"
import { RecipeDetailPage } from "@/pages/RecipeDetail"
import { InvitesPage } from "@/pages/Invites"
import { useEffect, useState } from "react"

// Match /recipes/{uuid}. UUIDs are 8-4-4-4-12 hex with hyphens.
const RECIPE_DETAIL_RE = /^\/recipes\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/**
 * Tiny client-side router (no library dep). Intercepts <a href=/...> clicks
 * and updates window.location.pathname → re-renders.
 */
function useLocation() {
  const [path, setPath] = useState(window.location.pathname)
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener("popstate", onPop)
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a") as HTMLAnchorElement | null
      if (!a) return
      const href = a.getAttribute("href")
      if (!href || !href.startsWith("/") || href.startsWith("//")) return
      if (a.target && a.target !== "_self") return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      e.preventDefault()
      if (window.location.pathname !== href) {
        window.history.pushState({}, "", href)
        setPath(href)
        window.scrollTo(0, 0)
      }
    }
    document.addEventListener("click", onClick)
    return () => {
      window.removeEventListener("popstate", onPop)
      document.removeEventListener("click", onClick)
    }
  }, [])
  return path
}

function Router() {
  const auth = useAuth()
  const path = useLocation()
  if (auth.status === "loading") return <LoadingSplash />
  if (auth.status === "anon") return <LoginPage />
  // Dynamic routes first (cheap regex; we only have one).
  const recipeMatch = path.match(RECIPE_DETAIL_RE)
  if (recipeMatch) return <RecipeDetailPage id={recipeMatch[1]} />
  switch (path) {
    case "/today":        return <TodayPage />
    case "/performance":  return <PerformancePage />
    case "/profile":      return <ProfilePage />
    case "/ingredients":  return <IngredientsPage />
    case "/weight":       return <WeightPage />
    case "/recipes/new":  return <RecipesNewPage />
    case "/recipes":      return <RecipesPage />
    case "/invites":      return <InvitesPage />
    case "/":
    default:              return <DashboardPage />
  }
}

function LoadingSplash() {
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <p className="eyebrow animate-ink-in">loading…</p>
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BackgroundWorkProvider>
        <Router />
      </BackgroundWorkProvider>
    </AuthProvider>
  )
}
